'use client';

import { cn, getBackgroundClass } from '@workspace/lib';
import { featuredBannerVariants } from '@workspace/blocks/FeaturedBanner/featuredBanner.variants';
import { Button, Picture, Typography } from '@workspace/ui/common';

import type { FeaturedBannerProps } from './featured-banner.types';

const mediaDimensions = {
  full: {
    desktopWidth: 720,
    desktopHeight: 900,
    mobileWidth: 390,
    mobileHeight: 496,
  },
  medium: {
    desktopWidth: 630,
    desktopHeight: 420,
    mobileWidth: 342,
    mobileHeight: 228,
  },
  slim: {
    desktopWidth: 490,
    desktopHeight: 326,
    mobileWidth: 342,
    mobileHeight: 192,
  },
} as const;

function formatHeading(text: string) {
  if (!text) return text;
  const withLineBreaks = text.replace(/\\n/g, '\n');
  return withLineBreaks.replace(/\s+([^\s]+)$/, '\u00A0$1');
}

export function FeaturedBanner({ block, index = 0 }: FeaturedBannerProps) {
  if (!block || typeof block !== 'object') return null;

  const {
    heading,
    description,
    blockAlignment = 'left',
    contentPosition = 'left',
    heroBannerTypeStyle = 'medium',
    backgroundColor = 'bg-white',
    image,
    buttons = [],
  } = block;

  const backgroundClass = getBackgroundClass(backgroundColor);

  const {
    container,
    grid,
    contentWrapper,
    contentInner,
    heading: headingSlot,
    description: descriptionSlot,
    buttonGroup,
    button,
    mediaWrapper,
  } = featuredBannerVariants({
    style: heroBannerTypeStyle,
    blockAlignment,
    contentPosition,
  });

  const dimensions = mediaDimensions[heroBannerTypeStyle];
  const isContained = heroBannerTypeStyle !== 'full';

  return (
    <div className={cn('w-full', backgroundClass)}>
      <section className={cn('w-full', isContained && 'porta-container', container())}>
        <div className={cn('grid grid-cols-1 items-center', grid())}>
          <div className={cn('order-1 relative w-full self-center overflow-hidden', mediaWrapper())}>
            {image && (
              <Picture
                image={image}
                alt={image.alt || heading || 'Featured banner image'}
                desktopWidth={dimensions.desktopWidth}
                desktopHeight={dimensions.desktopHeight}
                mobileWidth={dimensions.mobileWidth}
                mobileHeight={dimensions.mobileHeight}
                priority={index === 0}
                className="block size-full"
                imgClassName="size-full object-cover"
              />
            )}
          </div>

          {/* Content Column (order-2 on mobile, desktop order controlled by contentPosition) */}
          <div className={cn('order-2 flex flex-col justify-center', contentWrapper())}>
            <div className={cn('flex flex-col items-start justify-center', contentInner())}>
              {heading && (
                <Typography variant="h1" className={cn('w-full whitespace-pre-line text-balance', headingSlot())}>
                  {formatHeading(heading)}
                </Typography>
              )}

              {description && (
                <Typography variant="bodyLg" className={descriptionSlot()}>
                  {description}
                </Typography>
              )}

              {Array.isArray(buttons) && buttons.length > 0 && (
                <div className={cn('flex flex-col xs:flex-row flex-wrap w-full z-10', buttonGroup())}>
                  {buttons.slice(0, 3).map((btn, btnIdx) => {
                    const label = btn.label || '';
                    const url = btn.url || btn.link?.url;
                    const openInNewTab = btn.isOpenInNewTab ?? btn.link?.isOpenInNewTab;
                    return (
                      <Button
                        key={btn._key ?? btnIdx}
                        variant={btn.style}
                        url={url}
                        openInNewTab={openInNewTab}
                        className={button()}
                      >
                        {label}
                      </Button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
